import classNames from 'classnames';
import React, { useContext, useMemo } from 'react';

import { RicosContext } from './../../ricos-context';
import type { Node } from './../../ricos-schema/legacy/wix/rich_content/v1';
import type { ImageNode } from './../../ricos-schema/next-schema';
import type { Helpers, SEOSettings } from './../../ricos-types';
import { Image as ImageComp } from './../../ui-components';

import { RICOS_DEFAULTS } from '../consts';
import { EXPAND_BUTTON } from '../data-hooks';
import ExpandIcon from '../icons/expand';
import classes from '../statics/styles/image-viewer.rtlignore-scss';
import type { ImagePluginViewerConfig } from '../types';
import type { ImageClickActionConfig, ImageClickActionHandle } from './common-types';
import { ImageContainer } from './image-container';
import { useCustomWidthStyle } from './use-custom-width-style';
import { useFallbackWidthStyle } from './use-fallback-width-style';
import { useImageDimensions } from './use-image-dimensions';
import { useImageSocialAttributes } from './use-image-social-attributes';

type ImageViewerProps = {
  node: Node;
  isMobile: boolean;
  imageClickActionConfig: ImageClickActionConfig;
  shouldLazyLoadImages: boolean;
  helpers?: Helpers;
  config?: ImagePluginViewerConfig;
  expand?: ImageClickActionHandle;
  className?: string;
  dataUrl?: string;
  seoMode?: SEOSettings;
};

export const Image = ({
  dataUrl,
  node,
  className,
  config,
  helpers,
  isMobile,
  seoMode,
  imageClickActionConfig,
  expand,
  shouldLazyLoadImages,
}: ImageViewerProps) => {
  const containerData = node.imageData?.containerData || RICOS_DEFAULTS.containerData;
  const { image, altText, decorative, styles } = node.imageData || {};
  const { src } = image || {};

  const { t } = useContext(RicosContext);

  const imageSource = useMemo(() => {
    if (dataUrl) {
      return dataUrl;
    }

    const { url, id } = src || {};
    if (url) {
      return url;
    }
    const { getImageUrl } = helpers ?? {};

    if (getImageUrl && id) {
      return getImageUrl({ file_name: id });
    }

    return id;
  }, [
    dataUrl,
    node.imageData?.image?.src?.url,
    node.imageData?.image?.src?.id,
    helpers?.getImageUrl,
  ]);

  const { width, height } = useImageDimensions({
    src: imageSource,
    containerData,
    image,
  });
  const fallbackWidthStyle = useFallbackWidthStyle({ containerData, isMobile });
  const customWidthStyle = useCustomWidthStyle({ containerData, isMobile, width });
  const socialAttributes = useImageSocialAttributes({
    config,
    src,
    width: image?.width,
    height: image?.height,
  });

  const style = {
    ...(isValueSet(styles?.border?.radius)
      ? { '--ricos-image-border-radius': `${styles?.border?.radius}px` ?? 'unset' }
      : {}),
    ...(isValueSet(styles?.border?.width)
      ? { '--ricos-image-border-width': `${styles?.border?.width}px` ?? 'unset' }
      : {}),
    ...(isValueSet(styles?.border?.color)
      ? { '--ricos-image-border-color': styles?.border?.color ?? 'unset' }
      : {}),
    '--ricos-image-default-border-color': config?.colors?.color8 ?? 'unset',
  };

  const doDimensionsExist = width !== undefined && height !== undefined;

  return (
    <ImageContainer {...imageClickActionConfig} disableDownload={node.imageData?.disableDownload}>
      {doDimensionsExist && imageSource && (
        <ImageComp
          id={node.imageData?.image?.src?.id ?? imageSource}
          containerId={node.id}
          displayMode="fill"
          width={width}
          height={height}
          uri={node.imageData?.image?.src?.id ?? imageSource}
          alt={decorative ? '' : altText || toCaptionPlainText(node as ImageNode) || ''}
          role={decorative ? 'presentation' : undefined}
          containerProps={{
            className: classNames(classes.imageWrapper, classes.imageRatio, className, {
              [classes.pointer]: !!expand,
            }),
            containerId: node.id,
            style: {
              ['--dim-height' as string]: height,
              ['--dim-width' as string]: width,
              ...fallbackWidthStyle,
              ...customWidthStyle,
              ...style,
            },
            dataHook: `image-viewer-${node.id}`,
          }}
          imageStyles={{
            maxWidth: '100%',
          }}
          socialAttrs={{ ...socialAttributes, draggable: false }}
          isSEOBot={!!seoMode}
          encoding={config?.encoding}
          {...(shouldLazyLoadImages
            ? {
                placeholderTransition: seoMode ? undefined : 'blur',
                shouldUseLQIP: config?.disableTransition !== false,
              }
            : {})}
        />
      )}
      {!!expand && (
        <button
          className={classes.expandButton}
          onClick={expand}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              expand(e);
            }
          }}
          type="button"
          data-hook={EXPAND_BUTTON}
          aria-label={t('Image_Expand_Button_AriaLabel')}
        >
          <ExpandIcon className={classes.expandIcon} />
        </button>
      )}
    </ImageContainer>
  );
};

const toCaptionPlainText = (imageNode: ImageNode) => {
  if (!imageNode.nodes?.[0]?.nodes?.length) {
    return '';
  }

  return imageNode.nodes[0].nodes.map(node => node.textData.text).join('');
};

const isValueSet = (value: number | string | null | undefined) => {
  return value !== undefined && value !== null && value !== '';
};
